// client/src/views/Interactive_parts/ShapeEditToolbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getShapeType } from '../../components/pathing_tools/PathingManager';
import IconUpload from '../../components/IconUpload';

const RECENT_LINE_COLOURS_KEY = 'point_atlas_recent_line_colours';
const RECENT_FILL_COLOURS_KEY = 'point_atlas_recent_fill_colours';
const API_BASE = '/api/polygons';
const GROUND_FLOOR_OVERLAY_ID = 1;

function getRecentColours(key) {
  try {
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    if (Array.isArray(arr)) return arr;
  } catch (e) {}
  return [];
}

function addRecentColour(key, hex) {
  try {
    let arr = getRecentColours(key);
    arr = arr.filter(x => x !== hex);
    arr.unshift(hex);
    arr = arr.slice(0, 5);
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {}
}

function tryParseJSON(s, fallback) {
  try {
    return typeof s === 'string' ? JSON.parse(s) : (s ?? fallback);
  } catch {
    return fallback;
  }
}

const toolbarStyle = {
  position: 'absolute',
  top: 16,
  left: 70,
  display: 'flex',
  alignItems: 'center',
  background: '#191a1f',
  borderRadius: '12px',
  boxShadow: '0 2px 12px #000b',
  padding: '6px 14px 6px 14px',
  zIndex: 2500,
  gap: 12,
  minWidth: 420,
  height: 'auto',
  flexWrap: 'wrap',
};

function ColourPickerModal({
  value,
  recent,
  onChange,
  onCancel,
  onConfirm,
  title = 'Pick Colour',
}) {
  const [internalValue, setInternalValue] = useState(value);
  const originalValue = useRef(value);

  useEffect(() => {
    setInternalValue(value);
    originalValue.current = value;
  }, [value]);

  function handleCancel() {
    setInternalValue(originalValue.current);
    onChange(originalValue.current);
    onCancel();
  }

  function handleConfirm() {
    onConfirm(internalValue);
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(20,20,22,0.45)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#23242a',
          padding: 32,
          borderRadius: 13,
          boxShadow: '0 2px 20px #000a',
          minWidth: 320,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          alignItems: 'stretch',
        }}
      >
        <div style={{ color: '#ffe670', fontWeight: 700, fontSize: 19 }}>
          {title}
        </div>
        <input
          type="color"
          value={internalValue}
          onChange={e => {
            setInternalValue(e.target.value);
            onChange(e.target.value);
          }}
          style={{
            width: 90,
            height: 48,
            border: 'none',
            borderRadius: 12,
            alignSelf: 'center',
            background: 'none',
            cursor: 'pointer',
            boxShadow: '0 0 0 2px #444',
          }}
        />
        {recent.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 6,
            }}
          >
            {recent.map(hex => (
              <span
                key={hex}
                onClick={() => {
                  setInternalValue(hex);
                  onChange(hex);
                }}
                style={{
                  width: 28,
                  height: 28,
                  background: hex,
                  borderRadius: 8,
                  border:
                    hex === internalValue
                      ? '3px solid #ffe670'
                      : '1px solid #444',
                  display: 'inline-block',
                  cursor: 'pointer',
                  boxShadow: hex === '#fff' ? '0 0 0 1px #666' : '',
                }}
                title={hex}
              />
            ))}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 14,
            marginTop: 12,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              background: '#31313c',
              color: '#eee',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{
              background: '#ffe670',
              color: '#23242a',
              border: 'none',
              borderRadius: 7,
              padding: '8px 23px',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ShapeEditToolbar({
  shape,
  layer,
  onChange,
  onSave,
  onClose,
  onDelete,
  saving = false,
  canEdit = false,

  // Teleport dropdown sources:
  extraRegionMaps = [],
  parentMapId = null,
  parentMapName = 'Parent Map',
  currentMapId = null,

  // Overlay lock / "embed" selector
  overlayAssets = [],
  activeOverlayId = null,
}) {
  const safeShape = shape || {};

  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [tempLabel, setTempLabel] = useState(safeShape.label || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);

  const [colourModal, setColourModal] = useState(null);
  const [recentLineColours, setRecentLineColours] = useState(
    getRecentColours(RECENT_LINE_COLOURS_KEY)
  );
  const [recentFillColours, setRecentFillColours] = useState(
    getRecentColours(RECENT_FILL_COLOURS_KEY)
  );

  const [pathMode, setPathMode] = useState('zone');
  const [pathConfig, setPathConfig] = useState({
    progress: 0,
    // IMPORTANT: tokenSpeed is metres per second (m/s)
    speed: 1.5,
    autoMove: false,
    direction: 'forward',
  });

  const [showTokenIconModal, setShowTokenIconModal] = useState(false);

  // Teleport state (checkbox + destination map)
  const [teleportEnabled, setTeleportEnabled] = useState(
    !!(safeShape.teleportOnClick || safeShape.teleport_enabled) ||
    (safeShape.behaviorType === 'teleport' || safeShape.behavior_type === 'teleport')
  );
  const initialTeleportDest =
    (safeShape.behaviorProps &&
      safeShape.behaviorProps.destinationMapId) ||
    (safeShape.behavior_props &&
      tryParseJSON(safeShape.behavior_props, {}).destinationMapId) ||
    safeShape.teleportMapId ||
    safeShape.teleport_map_id ||
    '';
  const [teleportTargetId, setTeleportTargetId] = useState(initialTeleportDest);

  // Overlay lock state for this zone
  let initialOverlayId =
    safeShape.overlayId != null
      ? safeShape.overlayId
      : safeShape.overlay_id != null
      ? safeShape.overlay_id
      : null;

  if (initialOverlayId === '' || initialOverlayId === undefined) {
    initialOverlayId = null;
  }

  const [overlayLockEnabled, setOverlayLockEnabled] = useState(
    initialOverlayId != null // off by default unless shape already has overlay_id
  );
  const [overlayId, setOverlayId] = useState(initialOverlayId);

  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState('');

  const tokenIconUrl =
    (shape && (shape.tokenIconUrl || shape.token_icon_url)) || '';
  const mapIdForIcon = (shape && (shape.mapId || shape.map_id)) || null;
  const shapeIdForIcon = shape && (shape.id || shape.shapeId || shape.shape_id);

  useEffect(() => {
    setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
    setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
  }, []);

  // Keep overlay lock state in sync with shape + active overlay
  useEffect(() => {
    if (!shape) return;
    let fromShape =
      shape.overlayId != null
        ? shape.overlayId
        : shape.overlay_id != null
        ? shape.overlay_id
        : null;

    if (fromShape === '' || fromShape === undefined) {
      fromShape = null;
    }

    if (fromShape != null) {
      setOverlayLockEnabled(true);
      setOverlayId(fromShape);
    } else if (activeOverlayId != null && activeOverlayId !== '') {
      // Suggest current active overlay as default choice, but keep lock off
      setOverlayId(prev =>
        prev == null || prev === '' ? activeOverlayId : prev
      );
    }
  }, [shape, activeOverlayId]);

  useEffect(() => {
    if (!layer) return;
    const lineColor = safeShape.strokeColor || '#3388ff';
    const fillColor = safeShape.fillColor || '#3388ff';
    const strokeOpacity = safeShape.strokeOpacity ?? 1;
    const fillOpacity = safeShape.fillOpacity ?? 0.5;

    if (layer.setStyle) {
      const styleObj = {
        color: lineColor,
        fillColor: fillColor,
        opacity: strokeOpacity,
        fillOpacity: fillOpacity,
      };
      layer.setStyle(styleObj);
    }
    if (typeof window !== 'undefined' && window.L && layer instanceof window.L.Circle) {
      layer.setStyle({
        color: lineColor,
        fillColor: fillColor,
        opacity: strokeOpacity,
        fillOpacity: fillOpacity,
      });
      if (safeShape.radius) layer.setRadius(safeShape.radius);
    }
    if (layer && layer._popup && layer._popup.isOpen()) {
      layer._popup.setContent(safeShape.label || '');
    }
  }, [
    safeShape.strokeColor,
    safeShape.fillColor,
    safeShape.strokeOpacity,
    safeShape.fillOpacity,
    safeShape.radius,
    safeShape.label,
    layer,
  ]);

  useEffect(() => {
    if (!layer || !layer.pm) return;

    const shapeTypeLocal = getShapeType(layer);
    const allowSelfIntersection = shapeTypeLocal === 'polyline';

    if (canEdit && editing) {
      if (!layer.pm.enabled()) {
        layer.pm.enable({ allowSelfIntersection });
      } else if (typeof layer.pm.setOptions === 'function') {
        layer.pm.setOptions({ allowSelfIntersection });
      }
    } else {
      if (layer.pm.enabled()) {
        layer.pm.disable();
      }
    }

    return () => {
      if (layer && layer.pm && layer.pm.enabled()) {
        layer.pm.disable();
      }
    };
  }, [editing, layer, canEdit]);

  const shapeType = getShapeType(layer);
  const isClosedShape =
    shapeType === 'circle' ||
    shapeType === 'rectangle' ||
    shapeType === 'polygon';

  useEffect(() => {
    if (!shape) return;

    // Derive starting mode
    const modeFromShape =
      shape.behaviorType ||
      shape.behavior_type ||
      'zone';
    const resolvedMode = modeFromShape === 'pathing' ? 'pathing' : 'zone';
    setPathMode(resolvedMode);

    // Merge token/pathing fields from explicit fields or behaviorProps fallback
    const bp =
      shape.behaviorProps ||
      (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {}) ||
      {};

    const tokenDefaultPosition =
      typeof shape.tokenDefaultPosition === 'number'
        ? shape.tokenDefaultPosition
        : (typeof bp.tokenDefaultPosition === 'number' ? bp.tokenDefaultPosition : 0);

    const tokenSpeedRaw =
      typeof shape.tokenSpeed === 'number'
        ? shape.tokenSpeed
        : (typeof bp.tokenSpeed === 'number' ? bp.tokenSpeed : 1.5);

    const tokenAutoMove =
      typeof shape.tokenAutoMove === 'boolean'
        ? shape.tokenAutoMove
        : !!bp.tokenAutoMove;

    const tokenDirection =
      shape.tokenDirection ||
      bp.tokenDirection ||
      (isClosedShape ? 'clockwise' : 'forward');

    setPathConfig(prev => ({
      ...prev,
      progress: tokenDefaultPosition,
      speed: tokenSpeedRaw,
      autoMove: tokenAutoMove,
      direction: tokenDirection,
    }));

    // Keep teleport UI in sync with shape prop updates
    const enabled = !!(
      shape.behaviorType === 'teleport' ||
      shape.behavior_type === 'teleport' ||
      shape.teleportOnClick ||
      shape.teleport_enabled
    );
    const dest =
      (bp && bp.destinationMapId) ||
      shape.teleportMapId ||
      shape.teleport_map_id ||
      '';
    setTeleportEnabled(enabled);
    setTeleportTargetId(dest);
  }, [shape, isClosedShape]);

  // Early return AFTER hooks are declared (rules-of-hooks compliant)
  if (!shape || !canEdit) {
    return null;
  }

  // Build teleport destination options from props, skipping currentMapId
  const teleportOptions = (() => {
    const opts = [];
    const seen = new Set();

    if (parentMapId != null && parentMapId !== currentMapId) {
      const key = String(parentMapId);
      if (!seen.has(key)) {
        opts.push({
          id: parentMapId,
          name: parentMapName || 'Parent Map',
        });
        seen.add(key);
      }
    }

    if (Array.isArray(extraRegionMaps)) {
      for (const m of extraRegionMaps) {
        if (!m || m.id == null) continue;
        if (currentMapId != null && m.id === currentMapId) continue;
        const key = String(m.id);
        if (!seen.has(key)) {
          opts.push({
            id: m.id,
            name: m.name || 'Map',
          });
          seen.add(key);
        }
      }
    }
    return opts;
  })();

  const hasTeleportOptions = teleportOptions.length > 0;

  // Build list of overlay / "embed" options
  const overlayOptions = (() => {
    const opts = [];
    const seen = new Set();

    // Synthetic ground floor option (overlay_id = 1)
    const groundKey = String(GROUND_FLOOR_OVERLAY_ID);
    opts.push({
      id: GROUND_FLOOR_OVERLAY_ID,
      name: 'Ground Floor (Base Map)',
    });
    seen.add(groundKey);

    if (Array.isArray(overlayAssets)) {
      for (const ov of overlayAssets) {
        if (!ov || ov.id == null) continue;
        const key = String(ov.id);
        if (seen.has(key)) continue;
        opts.push({
          id: ov.id,
          name: ov.title || ov.name || `Overlay ${ov.id}`,
        });
        seen.add(key);
      }
    }
    return opts;
  })();

  function openLineColourModal() {
    setColourModal({
      type: 'line',
      original: shape.strokeColor || '#3388ff',
      value: shape.strokeColor || '#3388ff',
    });
  }

  function openFillColourModal() {
    setColourModal({
      type: 'fill',
      original: shape.fillColor || '#3388ff',
      value: shape.fillColor || '#3388ff',
    });
  }

  function handleColourChange(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      onChange({ ...shape, strokeColor: hex });
    } else {
      onChange({ ...shape, fillColor: hex });
    }
  }

  function handleColourCancel() {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      onChange({ ...shape, strokeColor: colourModal.original });
    } else {
      onChange({ ...shape, fillColor: colourModal.original });
    }
    setColourModal(null);
  }

  function handleColourConfirm(hex) {
    if (!colourModal) return;
    if (colourModal.type === 'line') {
      addRecentColour(RECENT_LINE_COLOURS_KEY, hex);
      setRecentLineColours(getRecentColours(RECENT_LINE_COLOURS_KEY));
      onChange({ ...shape, strokeColor: hex });
    } else {
      addRecentColour(RECENT_FILL_COLOURS_KEY, hex);
      setRecentFillColours(getRecentColours(RECENT_FILL_COLOURS_KEY));
      onChange({ ...shape, fillColor: hex });
    }
    setColourModal(null);
  }

  let currentLineMoveMode = 'pingpong';
  let currentLineDirection = 'forward';
  if (!isClosedShape && pathMode === 'pathing') {
    const dir = pathConfig.direction || 'forward';
    const isWrap =
      dir === 'wrap-forward' || dir === 'wrap-backward';
    currentLineMoveMode = isWrap ? 'wrap' : 'pingpong';
    const isBackward =
      dir === 'wrap-backward' ||
      dir === 'backward' ||
      dir === 'reverse';
    currentLineDirection = isBackward ? 'backward' : 'forward';
  }

  // Build payload for backend using camelCase fields (server maps to snake_case)
  function buildPayloadForBackend() {
    // Determine behaviorType and behaviorProps precedence:
    // Teleport takes precedence when enabled; otherwise use pathMode.
    let behaviorType = null;
    let behaviorProps = {};

    if (teleportEnabled) {
      behaviorType = 'teleport';
      if (teleportTargetId !== '' && teleportTargetId !== null && teleportTargetId !== undefined) {
        behaviorProps.destinationMapId = teleportTargetId;
      }
    } else if (pathMode === 'pathing') {
      behaviorType = 'pathing';
      behaviorProps.tokenDefaultPosition = pathConfig.progress;
      behaviorProps.tokenSpeed = Math.max(0, Number(pathConfig.speed) || 0); // m/s
      behaviorProps.tokenAutoMove = !!pathConfig.autoMove;
      behaviorProps.tokenDirection =
        pathConfig.direction || (isClosedShape ? 'clockwise' : 'forward');
    }

    // Overlay lock:
    // - If disabled → overlay_id null (always visible, regardless of overlays).
    // - If enabled  → use selected overlay; base map "ground floor" is id 1.
    let resolvedOverlayId =
      overlayLockEnabled &&
      overlayId !== null &&
      overlayId !== '' &&
      overlayId !== undefined
        ? overlayId
        : null;

    if (resolvedOverlayId != null && !Number.isNaN(Number(resolvedOverlayId))) {
      resolvedOverlayId = Number(resolvedOverlayId);
    }

    const payload = {
      mapId: shape.mapId || shape.map_id,
      userId: shape.userId || shape.user_id || null,
      label: shape.label || '',
      geometry: shape.geometry,
      fillColor: shape.fillColor ?? '#ff0000',
      strokeColor: shape.strokeColor ?? '#ff0000',
      strokeOpacity: shape.strokeOpacity ?? 1.0,
      fillOpacity: shape.fillOpacity ?? 0.5,
      pattern: shape.pattern || '',
      radius: shape.radius ?? null,
      shapeType: shape.shapeType || shape.shape_type || '',
      behaviorType: behaviorType,
      behaviorProps: behaviorProps,
      behaviorState:
        shape.behaviorState ??
        (shape.behavior_state ? tryParseJSON(shape.behavior_state, {}) : {}) ??
        {},
      sprite: shape.sprite ?? null,

      // Token icon URL saved alongside sprite
      tokenIconUrl:
        shape.tokenIconUrl ||
        shape.token_icon_url ||
        shape.sprite ||
        null,

      // Token fields mirrored as top-level (VTT serializer parity)
      tokenDefaultPosition:
        typeof pathConfig.progress === 'number' ? pathConfig.progress : undefined,
      tokenSpeed:
        typeof pathConfig.speed === 'number'
          ? Math.max(0, Number(pathConfig.speed) || 0)
          : undefined,
      tokenAutoMove:
        typeof pathConfig.autoMove === 'boolean' ? !!pathConfig.autoMove : undefined,
      tokenDirection:
        pathConfig.direction ||
        (isClosedShape ? 'clockwise' : 'forward'),

      // Overlay / embed awareness
      overlayId: resolvedOverlayId,
      overlay_id: resolvedOverlayId,
    };

    return payload;
  }

  async function handleSaveToDB() {
    setSaveError('');
    setSaveBusy(true);
    try {
      const payload = buildPayloadForBackend();
      if (!payload.mapId) {
        throw new Error('mapId missing on shape; cannot save.');
      }

      // Legacy mirror fields for UI parity (kept in local state and sent via onSave/onChange)
      const legacyTeleport =
        teleportEnabled && (teleportTargetId !== '' && teleportTargetId !== null && teleportTargetId !== undefined);

      const isUpdate = !!shape.id;
      const url = isUpdate ? `${API_BASE}/${shape.id}` : API_BASE;
      const method = isUpdate ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const saved = await res.json();

      const resolvedOverlayIdFromServer =
        saved.overlayId ??
        saved.overlay_id ??
        payload.overlayId ??
        payload.overlay_id ??
        shape.overlayId ??
        shape.overlay_id ??
        null;

      // Merge server response back into local shape (including behavior fields, token fields, etc.)
      const mergedLocal = {
        ...shape,
        id: saved.id ?? shape.id,
        label: saved.label ?? shape.label,
        strokeColor: saved.strokeColor ?? shape.strokeColor,
        fillColor: saved.fillColor ?? shape.fillColor,
        strokeOpacity: saved.strokeOpacity ?? shape.strokeOpacity,
        fillOpacity: saved.fillOpacity ?? shape.fillOpacity,
        geometry: saved.geometry ?? shape.geometry,
        pattern: saved.pattern ?? shape.pattern,
        radius: saved.radius ?? shape.radius,
        shapeType: saved.shapeType ?? shape.shapeType,
        behaviorType: saved.behaviorType ?? payload.behaviorType ?? null,
        behaviorProps: saved.behaviorProps ?? payload.behaviorProps ?? {},
        behaviorState: saved.behaviorState ?? payload.behaviorState ?? {},
        sprite: saved.sprite ?? payload.sprite ?? shape.sprite ?? null,

        tokenDefaultPosition:
          typeof saved.tokenDefaultPosition === 'number'
            ? saved.tokenDefaultPosition
            : (typeof payload.tokenDefaultPosition === 'number'
                ? payload.tokenDefaultPosition
                : shape.tokenDefaultPosition),
        tokenSpeed:
          typeof saved.tokenSpeed === 'number'
            ? saved.tokenSpeed
            : (typeof payload.tokenSpeed === 'number'
                ? payload.tokenSpeed
                : shape.tokenSpeed),
        tokenAutoMove:
          typeof saved.tokenAutoMove === 'boolean'
            ? saved.tokenAutoMove
            : (typeof payload.tokenAutoMove === 'boolean'
                ? payload.tokenAutoMove
                : shape.tokenAutoMove),
        tokenDirection:
          saved.tokenDirection || payload.tokenDirection || shape.tokenDirection,

        // Keep tokenIconUrl in sync with backend
        tokenIconUrl:
          saved.tokenIconUrl ??
          payload.tokenIconUrl ??
          shape.tokenIconUrl ??
          shape.token_icon_url ??
          null,

        // Overlay fields
        overlayId: resolvedOverlayIdFromServer,
        overlay_id: resolvedOverlayIdFromServer,

        // Legacy mirrors for UI parity
        teleportOnClick: legacyTeleport || !!shape.teleportOnClick || !!shape.teleport_enabled,
        teleport_enabled: legacyTeleport || !!shape.teleport_enabled || !!shape.teleportOnClick,
        teleportMapId: legacyTeleport ? teleportTargetId : (shape.teleportMapId ?? null),
        teleport_map_id: legacyTeleport ? teleportTargetId : (shape.teleport_map_id ?? null),

        updatedAt: saved.updatedAt ?? new Date().toISOString(),
      };

      // Push merged data up
      if (onSave) onSave(mergedLocal);
      if (onChange) onChange(mergedLocal);
    } catch (e) {
      setSaveError(e?.message || 'Failed to save');
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <div className="shape-edit-toolbar" style={toolbarStyle}>
      {/* Line colour + opacity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Line</span>
        <span
          onClick={openLineColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.strokeColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b',
          }}
          title="Line Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.strokeOpacity ?? 1}
          onChange={e =>
            onChange({
              ...shape,
              strokeOpacity: parseFloat(e.target.value),
            })
          }
          style={{ width: 60, marginLeft: 5 }}
          title="Line Opacity"
        />
        <span
          style={{
            color: '#ffe670',
            fontSize: 13,
            marginLeft: 4,
            display: 'inline-block',
            minWidth: 40,
            textAlign: 'right',
          }}
        >
          {Math.round((shape.strokeOpacity ?? 1) * 100)}%
        </span>
      </div>

      {/* Fill colour + opacity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: '#fff', fontSize: 13 }}>Fill</span>
        <span
          onClick={openFillColourModal}
          style={{
            display: 'inline-block',
            width: 26,
            height: 26,
            borderRadius: 7,
            background: shape.fillColor || '#3388ff',
            border: '1.5px solid #ffe670',
            cursor: 'pointer',
            boxShadow: '0 1px 6px #000b',
          }}
          title="Fill Colour"
        />
        <input
          type="range"
          min="0.05"
          max="1"
          step="0.01"
          value={shape.fillOpacity ?? 0.5}
          onChange={e =>
            onChange({
              ...shape,
              fillOpacity: parseFloat(e.target.value),
            })
          }
          style={{ width: 60, marginLeft: 5 }}
          title="Fill Opacity"
        />
        <span
          style={{
            color: '#ffe670',
            fontSize: 13,
            marginLeft: 4,
            display: 'inline-block',
            minWidth: 40,
            textAlign: 'right',
          }}
        >
          {Math.round((shape.fillOpacity ?? 0.5) * 100)}%
        </span>
      </div>

      {/* Label + edit name */}
      <span
        style={{
          background: '#24253a',
          color: '#fff',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 13px',
          fontWeight: 600,
          fontSize: 14,
          marginRight: 3,
        }}
      >
        {shape.label ? shape.label : 'Zone'}
      </span>
      <button
        onClick={() => setLabelModalOpen(true)}
        style={{
          background: '#24253a',
          color: '#fff',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 13px',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
          marginRight: 3,
        }}
        title="Edit Zone Name"
      >
        Edit Name
      </button>

      {/* Mode selector: Zone / Pathing */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: '#24253a',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 9px',
        }}
      >
        <span style={{ color: '#fff', fontSize: 13, marginRight: 4 }}>
          Mode
        </span>
        <select
          value={pathMode}
          onChange={e => setPathMode(e.target.value)}
          style={{
            background: '#16171b',
            color: '#ffe670',
            border: '1px solid #393b49',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 13,
            fontWeight: 600,
            outline: 'none',
            cursor: 'pointer',
          }}
          title="Zone or Pathing"
        >
          <option value="zone">Zone</option>
          <option value="pathing">Pathing</option>
        </select>
      </div>

      {/* Overlay / embed lock + selector */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#24253a',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 9px',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            fontSize: 13,
            marginRight: 4,
          }}
        >
          <input
            type="checkbox"
            checked={overlayLockEnabled}
            onChange={e => {
              const checked = e.target.checked;
              let nextOverlayId = overlayId;

              if (checked) {
                // When enabling:
                // - Prefer an explicit selection if already present
                // - Else prefer active overlay if any
                // - Else treat as "lock to ground floor" → overlay_id = 1
                if (
                  nextOverlayId === null ||
                  nextOverlayId === undefined ||
                  nextOverlayId === ''
                ) {
                  if (activeOverlayId != null && activeOverlayId !== '') {
                    nextOverlayId = activeOverlayId;
                  } else {
                    nextOverlayId = GROUND_FLOOR_OVERLAY_ID;
                  }
                }
              } else {
                // When disabling: clear overlay id → always visible
                nextOverlayId = null;
              }

              setOverlayLockEnabled(checked);
              setOverlayId(nextOverlayId);

              const numeric =
                nextOverlayId === null ||
                nextOverlayId === '' ||
                nextOverlayId === undefined
                  ? null
                  : (Number.isNaN(Number(nextOverlayId))
                      ? nextOverlayId
                      : Number(nextOverlayId));

              onChange({
                ...shape,
                overlayId: numeric,
                overlay_id: numeric,
              });
            }}
          />
          <span>Lock to overlay</span>
        </label>
        {overlayLockEnabled ? (
          overlayOptions.length > 0 ? (
            <select
              value={
                overlayId !== null &&
                overlayId !== undefined &&
                overlayId !== ''
                  ? String(overlayId)
                  : ''
              }
              onChange={e => {
                const val = e.target.value || '';
                setOverlayId(val === '' ? null : val);
                const numeric =
                  val === ''
                    ? null
                    : (Number.isNaN(Number(val)) ? val : Number(val));
                onChange({
                  ...shape,
                  overlayId: numeric,
                  overlay_id: numeric,
                });
              }}
              style={{
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: 13,
                fontWeight: 600,
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Which overlay / embed this zone belongs to"
            >
              <option value="">Choose overlay…</option>
              {overlayOptions.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          ) : (
            <span
              style={{
                color: '#ffb3b3',
                fontSize: 12,
                fontStyle: 'italic',
              }}
            >
              No overlays available
            </span>
          )
        ) : (
          <span
            style={{
              color: '#aaa',
              fontSize: 12,
              fontStyle: 'italic',
            }}
          >
            Visible on all overlays
          </span>
        )}
      </div>

      {/* Teleport on click + map select */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#24253a',
          border: '1px solid #393b49',
          borderRadius: 7,
          padding: '5px 9px',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            fontSize: 13,
            marginRight: 4,
          }}
        >
          <input
            type="checkbox"
            checked={teleportEnabled}
            onChange={e => {
              const checked = e.target.checked;
              setTeleportEnabled(checked);
              const next = {
                ...shape,
                // legacy mirrors
                teleportOnClick: checked,
                teleport_enabled: checked,
              };
              // set behaviorType immediately for parity (final payload built in save)
              if (checked) {
                next.behaviorType = 'teleport';
                next.behavior_props = JSON.stringify({
                  ...(shape.behaviorProps || {}),
                  destinationMapId: teleportTargetId || null,
                });
              } else if (pathMode === 'pathing') {
                next.behaviorType = 'pathing';
              } else {
                next.behaviorType = null;
              }
              onChange(next);
            }}
          />
          Move to map on
        </label>
        {teleportEnabled &&
          (hasTeleportOptions ? (
            <select
              value={teleportTargetId || ''}
              onChange={e => {
                const val = e.target.value || '';
                setTeleportTargetId(val);
                const numeric =
                  val === '' ? null : Number.isNaN(Number(val)) ? val : Number(val);
                const existingBP =
                  shape.behaviorProps ||
                  (shape.behavior_props ? tryParseJSON(shape.behavior_props, {}) : {}) ||
                  {};
                onChange({
                  ...shape,
                  // legacy mirrors
                  teleportMapId: numeric,
                  teleport_map_id: numeric,
                  // also reflect into behaviorProps live
                  behaviorProps: {
                    ...existingBP,
                    destinationMapId: numeric,
                  },
                });
              }}
              style={{
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '4px 8px',
                fontSize: 13,
                fontWeight: 600,
                outline: 'none',
                cursor: 'pointer',
              }}
              title="Destination map when clicking this shape"
            >
              <option value="">Choose map…</option>
              {teleportOptions.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          ) : (
            <span
              style={{
                color: '#ffb3b3',
                fontSize: 12,
                fontStyle: 'italic',
              }}
            >
              No maps available
            </span>
          ))}
      </div>

      {/* Pathing controls */}
      {pathMode === 'pathing' && (
        <>
          {/* Position slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Pos</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={pathConfig.progress}
              onChange={e =>
                setPathConfig(prev => ({
                  ...prev,
                  progress: parseFloat(e.target.value),
                }))
              }
              style={{ width: 90, marginLeft: 5 }}
              title="Path Position"
            />
            <span
              style={{
                color: '#ffe670',
                fontSize: 13,
                marginLeft: 4,
                display: 'inline-block',
                minWidth: 40,
                textAlign: 'right',
              }}
            >
              {Math.round(pathConfig.progress * 100)}%
            </span>
          </div>

          {/* Speed slider (metres per second) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: '#fff', fontSize: 13 }}>Speed</span>
            <input
              type="range"
              min="0"
              max="20"
              step="0.1"
              value={pathConfig.speed}
              onChange={e =>
                setPathConfig(prev => ({
                  ...prev,
                  speed: Math.max(0, Number(e.target.value) || 0),
                }))
              }
              style={{ width: 90, marginLeft: 5 }}
              title="Speed (m/s)"
            />
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              value={pathConfig.speed}
              onChange={e =>
                setPathConfig(prev => ({
                  ...prev,
                  speed: Math.max(0, Number(e.target.value) || 0),
                }))
              }
              style={{
                width: 70,
                background: '#16171b',
                color: '#ffe670',
                border: '1px solid #393b49',
                borderRadius: 6,
                padding: '4px 6px',
                fontSize: 13,
                fontWeight: 600,
                outline: 'none',
                textAlign: 'right',
              }}
              title="Speed (metres per second)"
            />
            <span style={{ color: '#ffe670', fontSize: 13 }}>m/s</span>
          </div>

          {/* Auto-move toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#fff',
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={pathConfig.autoMove}
              onChange={e =>
                setPathConfig(prev => ({
                  ...prev,
                  autoMove: e.target.checked,
                }))
              }
            />
            Auto-move
          </label>

          {/* Line move controls for non-closed (line) shapes */}
          {!isClosedShape && (
            <>
              {/* Pattern: ping-pong vs wrap */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#24253a',
                  border: '1px solid #393b49',
                  borderRadius: 7,
                  padding: '5px 9px',
                }}
              >
                <span style={{ color: '#fff', fontSize: 13 }}>
                  Line Move
                </span>
                <select
                  value={currentLineMoveMode}
                  onChange={e => {
                    const pattern = e.target.value;
                    setPathConfig(prev => {
                      const prevDir = prev.direction || 'forward';
                      const wasBackward =
                        prevDir === 'wrap-backward' ||
                        prevDir === 'backward' ||
                        prevDir === 'reverse';
                      const newDirection =
                        pattern === 'wrap'
                          ? wasBackward
                            ? 'wrap-backward'
                            : 'wrap-forward'
                          : wasBackward
                          ? 'backward'
                          : 'forward';
                      return {
                        ...prev,
                        direction: newDirection,
                      };
                    });
                  }}
                  style={{
                    background: '#16171b',
                    color: '#ffe670',
                    border: '1px solid #393b49',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 13,
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  title="How the token moves along the line"
                >
                  <option value="pingpong">Ping-pong</option>
                  <option value="wrap">Wrap (jump ends)</option>
                </select>
              </div>

              {/* Direction: forward vs backward */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#24253a',
                  border: '1px solid #393b49',
                  borderRadius: 7,
                  padding: '5px 9px',
                }}
              >
                <span style={{ color: '#fff', fontSize: 13 }}>
                  Direction
                </span>
                <select
                  value={currentLineDirection}
                  onChange={e => {
                    const dirChoice = e.target.value;
                    setPathConfig(prev => {
                      const prevDir = prev.direction || 'forward';
                      const pattern =
                        prevDir === 'wrap-forward' ||
                        prevDir === 'wrap-backward'
                          ? 'wrap'
                          : 'pingpong';
                      const newDirection =
                        pattern === 'wrap'
                          ? dirChoice === 'backward'
                            ? 'wrap-backward'
                            : 'wrap-forward'
                          : dirChoice === 'backward'
                          ? 'backward'
                          : 'forward';
                      return {
                        ...prev,
                        direction: newDirection,
                      };
                    });
                  }}
                  style={{
                    background: '#16171b',
                    color: '#ffe670',
                    border: '1px solid #393b49',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 13,
                    fontWeight: 600,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  title="Initial direction along the line"
                >
                  <option value="forward">Forward</option>
                  <option value="backward">Backward</option>
                </select>
              </div>
            </>
          )}

          {/* Direction selector for closed shapes */}
          {isClosedShape && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#24253a',
                border: '1px solid #393b49',
                borderRadius: 7,
                padding: '5px 9px',
              }}
            >
              <span style={{ color: '#fff', fontSize: 13 }}>
                Direction
              </span>
              <select
                value={pathConfig.direction || 'clockwise'}
                onChange={e =>
                  setPathConfig(prev => ({
                    ...prev,
                    direction: e.target.value,
                  }))
                }
                style={{
                  background: '#16171b',
                  color: '#ffe670',
                  border: '1px solid #393b49',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 13,
                  fontWeight: 600,
                  outline: 'none',
                  cursor: 'pointer',
                }}
                title="Path Direction"
              >
                <option value="clockwise">Clockwise</option>
                <option value="counterclockwise">
                  Counter-clockwise
                </option>
              </select>
            </div>
          )}

          {/* Token icon controls */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: '#24253a',
              border: '1px solid #393b49',
              borderRadius: 7,
              padding: '5px 9px',
            }}
          >
            <span style={{ color: '#fff', fontSize: 13 }}>Token</span>
            {tokenIconUrl ? (
              <img
                src={tokenIconUrl}
                alt="Token Icon"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  border: '1px solid #000a',
                  background: '#000',
                  objectFit: 'contain',
                }}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <span
                style={{
                  color: '#888',
                  fontSize: 12,
                  fontStyle: 'italic',
                }}
              >
                No icon
              </span>
            )}
            <button
              onClick={() => setShowTokenIconModal(true)}
              disabled={!mapIdForIcon || !shapeIdForIcon}
              title={
                !mapIdForIcon || !shapeIdForIcon
                  ? 'Save the shape first before adding an icon.'
                  : 'Change token icon'
              }
              style={{
                background:
                  !mapIdForIcon || !shapeIdForIcon ? '#333' : '#ffe670',
                color: '#23242a',
                border: 'none',
                borderRadius: 6,
                padding: '4px 10px',
                fontWeight: 600,
                fontSize: 13,
                cursor:
                  !mapIdForIcon || !shapeIdForIcon ? 'not-allowed' : 'pointer',
              }}
            >
              Change Icon
            </button>
          </div>
        </>
      )}

      {/* Edit / Save / Delete / Close buttons */}
      <button
        onClick={() => setEditing(e => !e)}
        style={{
          background: editing ? '#ffe670' : '#393b49',
          color: editing ? '#23242a' : '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 17px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 10,
          cursor: 'pointer',
        }}
        title={editing ? 'Finish Editing Shape' : 'Edit Shape'}
      >
        {editing ? 'Finish' : 'Edit'}
      </button>
      <button
        onClick={handleSaveToDB}
        disabled={saving || saveBusy}
        style={{
          background: '#4cc153',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          cursor: (saving || saveBusy) ? 'not-allowed' : 'pointer',
          marginLeft: 10,
        }}
        title="Save to Database"
      >
        {(saving || saveBusy) ? 'Saving...' : 'Save'}
      </button>
      {saveError && (
        <span style={{ color: '#ff8c8c', fontSize: 12, marginLeft: 6 }}>
          {saveError}
        </span>
      )}
      <button
        onClick={() => setShowDeleteModal(true)}
        style={{
          background: '#e04c53',
          color: '#fff',
          border: 'none',
          borderRadius: 7,
          padding: '6px 18px',
          fontWeight: 700,
          fontSize: 16,
          marginLeft: 8,
          cursor: 'pointer',
        }}
        title="Delete"
      >
        Delete
      </button>
      <button
        onClick={onClose}
        style={{
          background: 'none',
          color: '#fff',
          border: 'none',
          fontSize: 20,
          marginLeft: 2,
          cursor: 'pointer',
          padding: 2,
          borderRadius: 6,
          lineHeight: 1,
        }}
        title="Close"
      >
        ×
      </button>

      {/* Label modal */}
      {labelModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(20,20,22,0.45)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#23242a',
              padding: 28,
              borderRadius: 13,
              boxShadow: '0 2px 20px #000a',
              minWidth: 300,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              alignItems: 'stretch',
            }}
          >
            <div
              style={{
                color: '#ffe670',
                fontWeight: 700,
                fontSize: 19,
                marginBottom: 9,
              }}
            >
              Edit Zone Name
            </div>
            <input
              type="text"
              value={tempLabel}
              onChange={e => setTempLabel(e.target.value)}
              placeholder="Zone Name"
              style={{
                fontSize: 17,
                padding: '7px 12px',
                borderRadius: 7,
                border: '1px solid #393b49',
                outline: 'none',
                background: '#16171b',
                color: '#ffe670',
                fontWeight: 600,
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 10,
              }}
            >
              <button
                onClick={() => setLabelModalOpen(false)}
                style={{
                  background: '#31313c',
                  color: '#eee',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setLabelModalOpen(false);
                  onChange({ ...shape, label: tempLabel });
                }}
                style={{
                  background: '#ffe670',
                  color: '#23242a',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Set Name
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(24, 16, 20, 0.66)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: '#222124',
              padding: 38,
              borderRadius: 13,
              boxShadow: '0 2px 30px #000a',
              minWidth: 350,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 24,
            }}
          >
            <div
              style={{
                color: '#ffe670',
                fontWeight: 700,
                fontSize: 21,
                marginBottom: 8,
                textAlign: 'center',
              }}
            >
              Are you sure you want to delete this zone?
            </div>
            <div
              style={{
                display: 'flex',
                gap: 14,
                marginTop: 6,
              }}
            >
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: '#31313c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  if (onDelete) onDelete(shape, layer);
                }}
                style={{
                  background: '#e04c53',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 23px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token icon upload modal */}
      {showTokenIconModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(20,20,22,0.45)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ maxWidth: '90vw' }}>
            <IconUpload
              initialIconUrl={tokenIconUrl}
              mapId={mapIdForIcon}
              shapeId={shapeIdForIcon}
              onIconUploaded={url => {
                onChange({
                  ...shape,
                  tokenIconUrl: url,
                  token_icon_url: url,
                  sprite: url,
                });
                setShowTokenIconModal(false);
              }}
              disabled={!mapIdForIcon || !shapeIdForIcon}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                onClick={() => setShowTokenIconModal(false)}
                style={{
                  background: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '6px 17px',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Colour picker modal */}
      {colourModal && (
        <ColourPickerModal
          value={
            colourModal.type === 'line'
              ? shape.strokeColor || '#3388ff'
              : shape.fillColor || '#3388ff'
          }
          recent={
            colourModal.type === 'line'
              ? recentLineColours
              : recentFillColours
          }
          onChange={handleColourChange}
          onCancel={handleColourCancel}
          onConfirm={handleColourConfirm}
          title={
            colourModal.type === 'line'
              ? 'Pick Line Colour'
              : 'Pick Fill Colour'
          }
        />
      )}
    </div>
  );
}
